import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PayloadAddEditUserExtern, UserExternProps} from '@core/models/customer-extern';
import {TableLazyLoadEvent, TableModule} from "primeng/table";
import {debounceTime, distinctUntilChanged, Subject, Subscription} from 'rxjs';
import {MessageService} from "primeng/api";
import {Filter} from "@core/services/demanda.service";
import {UsuarioExternoService} from "@core/services/usuario-externo.service";
import {Skeleton} from "primeng/skeleton";
import {Tooltip} from "primeng/tooltip";
import {Button} from "primeng/button";
import {NgClass} from "@angular/common";
import {IconField} from "primeng/iconfield";
import {InputIcon} from "primeng/inputicon";
import {Select} from "primeng/select";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {Card} from "primeng/card";
import {InputText} from "primeng/inputtext";
import {Fluid} from "primeng/fluid";
import {PageProps} from "@core/models/pagination";
import {DialogComponent} from "@components/dialogs/dialog/dialog.component";
import {IconCheckedCircle} from "@components/icons/icon-checked-circle/icon-checked-circle.component";
import {IconDisabled} from "@components/icons/icon-disabled/icon-disabled.component";
import {IconBoldRam2Component} from "@components/icons/icon-bold-ram-2/icon-bold-ram-2.component";
import {IconEyeComponent} from "@components/icons/icon-eye/icon-eye.component";
import {IconPencilComponent} from "@components/icons/icon-pencil/icon-pencil.component";
import {ConfirmDialog} from '@components/dialogs/confirm-dialog/confirm-dialog';
import {IconBoldCloseSquareComponent} from '@components/icons/icon-bold-close-square/icon-bold-close-square.component';
import {IconBoldAddSquare} from '@components/icons/icon-bold-add-square/icon-bold-add-square';
import {MSG_REQUIRED_FIELDS_FORM} from '@shared/constants/messages';
import {TableSortResetter} from '@shared/utils/table-sort-resetter';

type FilterValue = string | number | boolean | null;

@Component({
    selector: 'app-controle-usuarios',
    imports: [
        Skeleton,
        Tooltip,
        IconField,
        InputIcon,
        Select,
        FormsModule,
        TableModule,
        Card,
        InputText,
        Fluid,
        DialogComponent,
        IconCheckedCircle,
        IconDisabled,
        NgClass,
        ReactiveFormsModule,
        IconBoldRam2Component,
        IconEyeComponent,
        IconPencilComponent,
        Button,
        ConfirmDialog,
        IconBoldCloseSquareComponent,
        IconBoldAddSquare
    ],
    templateUrl: './controle-usuarios.component.html',
    styleUrl: './controle-usuarios.component.css'
})
export class ControleUsuariosComponent implements OnInit, OnDestroy, AfterViewInit {
    customers: UserExternProps[] = [];
    loading: boolean = true;
    totalRecords: number = 0;
    selected: UserExternProps | null = null;

    @ViewChild('dt') dt!: any;

    public userToToggleStatus: UserExternProps | null = null;
    public subscriptions: Subscription[] = [];
    public currentPage = 0;
    public currentSize = 10;
    public currentSortField: string | null | undefined = undefined;
    public currentSortDirection: string | null | undefined = undefined;
    public currentFilters: { [key: string]: FilterValue } = {};
    perfilOptions = [
        {label: 'Todos', value: null},
        {label: 'Admin', value: true},
        {label: 'User', value: false}
    ];
    showAddEditViewDialog: boolean = false;
    loadingModal: boolean = false;
    headerTitleAddEditViewDialog: string = 'Adicionar Usuário';
    modalMode: 'add' | 'edit' | 'view' = 'add';
    formAddEditView!: FormGroup;
    showAddConfirmDialog: boolean = false;
    showEditConfirmDialog: boolean = false;
    showCancelAddConfirmDialog: boolean = false;
    showDisableConfirmDialog: boolean = false;
    showEnableConfirmDialog: boolean = false;
    showCancelEditConfirmDialog: boolean = false;
    private readonly filterSubjects: { [key: string]: Subject<FilterValue> } = {
        tipoRepresentacao: new Subject<FilterValue>(),
        nome: new Subject<FilterValue>(),
        area: new Subject<FilterValue>(),
        cargo: new Subject<FilterValue>(),
        situacao: new Subject<FilterValue>(),
        administrador: new Subject<FilterValue>(),
    };
    private isInitializing: boolean = true;
    private readonly sortResetter = new TableSortResetter();

    constructor(
        private readonly service: UsuarioExternoService,
        private readonly messageService: MessageService,
        private readonly formBuilder: FormBuilder,
    ) {

    }

    ngOnInit() {
        this.initAddViewEditForm();
        for (const key of Object.keys(this.filterSubjects)) {
            const subscription = this.filterSubjects[key]
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
                .subscribe(value => {
                    if (value !== null && value !== undefined && String(value).trim() !== '') {
                        this.currentFilters[key] = value;
                    } else {
                        delete this.currentFilters[key];
                    }

                    this.currentPage = 0;
                    if (this.dt) {
                        this.dt.first = 0;
                    }
                    if ((!value) || (value && String(value).length > 2)) {
                        this.loadUsers(this.currentPage, this.currentSize, this.currentSortField, this.currentSortDirection, this.currentFilters);
                    }
                });
            this.subscriptions.push(subscription);
        }

    }

    ngAfterViewInit(): void {
        this.loadUsers(this.currentPage, this.currentSize, this.currentSortField, this.currentSortDirection, this.currentFilters);
        setTimeout(() => this.isInitializing = false, 0);
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
    }

    loadUsers(
        page: number,
        size: number,
        sortField: string | null | undefined,
        sortDirection: string | null | undefined,
        filters: { [key: string]: FilterValue }
    ): void {
        this.loading = true;
        const filterArray: Filter[] = [];

        for (const key in filters) {
            if (Object.hasOwn(filters, key) &&
                (filters[key] !== null && filters[key] !== undefined && String(filters[key]).trim() !== '')) {

                let fieldNameForApi = key;
                if (key === 'tipoRepresentacao') {
                    fieldNameForApi = 'representacao';
                }
                filterArray.push({field: fieldNameForApi, value: filters[key]});
            }
        }

        const {sortField: sortFieldToUse, sortDirection: sortDirectionToUse} =
            this.resolveSortParams(sortField, sortDirection, filterArray);

        this.service.getUsuariosExternos(page, size,
            sortFieldToUse, sortDirectionToUse, filterArray).subscribe({
            next: (response: PageProps<UserExternProps>) => {
                this.customers = response.content;
                this.totalRecords = response.totalElements;
                this.loading = false;
            },
            error: (error: any) => {
                console.error('Erro ao carregar usuários externos:', error);
                this.loading = false;
                this.messageService.add({
                    severity: 'error', summary: 'Erro!',
                    detail: 'Falha ao carregar usuários.'
                });
            }
        });
    }

    onLazyLoad(event: TableLazyLoadEvent): void {
        if (this.isInitializing) {
            return;
        }
        this.currentPage = (event.first ?? 0) / (event.rows ?? this.currentSize);
        this.currentSize = event.rows ?? this.currentSize;

        let incomingField: string | undefined;
        if (typeof event.sortField === 'string') {
            incomingField = event.sortField;
        } else if (Array.isArray(event.sortField) && event.sortField.length > 0) {
            incomingField = event.sortField[0];
        }
        const incomingOrder = event.sortOrder ?? 0;

        if (this.sortResetter.reset(incomingField, incomingOrder)) {
            this.dt.sortField = undefined;
            this.dt.sortOrder = 0;
            this.currentSortField = undefined;
            this.currentSortDirection = undefined;
            this.loadUsers(this.currentPage, this.currentSize, undefined, undefined, this.currentFilters);
            return;
        }

        this.currentSortField = incomingField;

        if (event.sortOrder === 1) {
            this.currentSortDirection = 'asc';
        } else if (event.sortOrder === -1) {
            this.currentSortDirection = 'desc';
        } else {
            this.currentSortDirection = null;
        }

        this.loadUsers(
            this.currentPage,
            this.currentSize,
            this.currentSortField,
            this.currentSortDirection,
            this.currentFilters
        );
    }

    onFilterInput(eventOrValue: Event | { target: { value: FilterValue } } | FilterValue, field: string) {
        let value: FilterValue;
        if (eventOrValue instanceof Event) {
            value = (eventOrValue.target as HTMLInputElement | null)?.value ?? null;
        } else if (typeof eventOrValue === 'object' && eventOrValue !== null && 'target' in eventOrValue) {
            value = eventOrValue.target.value;
        } else {
            value = eventOrValue;
        }


        this.filterSubjects[field].next(value);
    }

    requestEnable(item: any): void {
        this.selected = item;
        this.showEnableConfirmDialog = true;
    }

    requestDisable(item: any): void {
        this.selected = item;
        this.showDisableConfirmDialog = true;
    }

    confirmEnable(item: any): void {
        this.applyUsuarioStatus(item, true, 'Usuário ativado com sucesso.');
    }

    confirmDisable(item: any): void {
        this.applyUsuarioStatus(item, false, 'Usuário inativado com sucesso.');
    }

    private applyUsuarioStatus(item: any, ativo: boolean, successDetail: string): void {
        if (!item?.id) {
            console.error('Erro: O ID do usuário não foi encontrado para ativar/desativar.');
            this.messageService.add({
                    severity: 'error',
                    summary: 'Erro!',
                    detail: 'Erro ao desativar/ativar usuário.'
                }
            );
            return;
        }

        this.service.ativarDesativarUsuario(item.id, ativo).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso!',
                    detail: successDetail
                });
            },
            error: (err: any) => {
                console.error('Erro ao atualizar status do usuário:', err);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro!',
                    detail: err.error?.message || 'Falha ao atualizar status do usuário.'
                });
            },
            complete: () => {
                this.loadUsers(this.currentPage, this.currentSize, this.currentSortField, this.currentSortDirection, this.currentFilters);
            }
        });
    }

    openAddEditViewDialog(mode: 'edit' | 'view' | 'add', item: any = null): void {
        this.modalMode = mode;
        this.selected = item;
        switch (mode) {
            case 'edit':
                this.headerTitleAddEditViewDialog = 'Editar Usuário'
                break;
            case 'view':
                this.headerTitleAddEditViewDialog = 'Visualizar Usuário'
                break;
            case 'add':
            default:
                this.headerTitleAddEditViewDialog = 'Adicionar Usuário'
        }
        const disable = mode === 'view';
        this.initAddViewEditForm(item, disable);
        this.loadingModal = true;
        this.showAddEditViewDialog = true;
        setTimeout(() => this.loadingModal = false, 300);
    }

    initAddViewEditForm(item?: any, disable = false): void {
        this.formAddEditView = this.formBuilder.group({
            nome: [{value: item?.nome || '', disabled: disable}, Validators.required],
            email: [{value: item?.email || '', disabled: disable}, [Validators.required, Validators.email]],
            area: [{value: item?.area || null, disabled: disable}, Validators.required],
            cargo: [{value: item?.cargo || null, disabled: disable}, Validators.required],
            observacao: [{value: item?.observacao || '', disabled: disable}]
        })
    }

    clearFilter(field: string): void {
        if (this.currentFilters[field] === undefined) return;
        this.currentFilters[field] = null;
        this.onFilterInput(null, field);
    }

    closeAddEditViewDialog() {
        this.showAddEditViewDialog = false;
        this.formAddEditView.reset();
        this.selected = null;

    }

    onSaveAddEdit() {
        if (this.formAddEditView.invalid) {
            this.messageService.add({
                severity: 'warn',
                detail: MSG_REQUIRED_FIELDS_FORM,
                summary: 'Atenção!'
            });
            this.formAddEditView.markAllAsTouched();
            return;
        }
        if (this.modalMode === 'edit') {
            this.showEditConfirmDialog = true;
            return;
        }
        this.showAddConfirmDialog = true;
    }

    onConfirmAddEdit(mode: 'add' | 'edit'): void {
        const payload: PayloadAddEditUserExtern =
            this.getPayloadFromDataAndForm(this.selected!);
        if (mode === 'edit') {
            this.edit(payload, this.selected?.id);
            this.closeAddEditViewDialog();
            return;
        }
        this.create(payload);
        this.closeAddEditViewDialog();
    }

    create(payload: PayloadAddEditUserExtern): void {
        this.service.createUsuarioExterno(payload).subscribe({
            next: (response) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso!',
                    detail: 'Usuário criado com sucesso.',
                });
            },
            error: (err: any) => {
                console.error('Erro ao cadastrar usuário: ', err);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro!',
                    detail: err.error?.message || 'Erro ao adicionar usuário.'
                })
            },
            complete: () => {
                this.loadUsers(this.currentPage, this.currentSize, this.currentSortField, this.currentSortDirection, this.currentFilters);
            }
        });
    }

    edit(payload: PayloadAddEditUserExtern, id?: number): void {
        if (!id) return;
        this.service.editUsuarioExterno(id, payload).subscribe({
            next: (response) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso!',
                    detail: 'Usuário editado com sucesso.',
                });
            },
            error: (err: any) => {
                console.error('Erro ao editar usuário: ', err);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro!',
                    detail: err.error?.message || 'Erro ao editar usuário.'
                })
            },
            complete: () => {
                this.loadUsers(this.currentPage, this.currentSize, this.currentSortField, this.currentSortDirection, this.currentFilters);
            }
        });
    }

    getPayloadFromDataAndForm(data: UserExternProps): PayloadAddEditUserExtern {
        const {nome, email, area, cargo, observacao} = this.formAddEditView.value;
        return {
            nome: nome || null,
            concessionariaId: data?.concessionariaId || null,
            email: email || null,
            area: area || null,
            cargo: cargo || null,
            observacao: observacao || null,
            tipoRepresentacao: data?.tipoRepresentacao || null,
            administrador: data?.administrador || null,
            outro: data?.outro || null,

        }
    }

    showAlertToast(message: string): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Atenção!',
            detail: message
        });
    }

    onCancelAddEditViewDialog() {
        if (this.modalMode === 'edit') {
            this.showCancelEditConfirmDialog = true;
            return;
        }
        this.showCancelAddConfirmDialog = true;
    }

    protected isFormControlInvalid(controlName: string) {
        const control = this.formAddEditView.get(controlName);
        return control?.touched && control?.invalid;
    }

    private resolveSortParams(
        sortField: string | null | undefined,
        sortDirection: string | null | undefined,
        filters: Filter[],
    ): {
        sortField: string | null | undefined;
        sortDirection: string | null | undefined;
    } {
        const hasUserSort =
            !!sortField && (sortDirection === 'asc' || sortDirection === 'desc');
        if (filters.length === 0 && !hasUserSort) {
            return {sortField: 'id', sortDirection: 'desc'};
        }
        return {sortField, sortDirection};
    }
}
